import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ref, getDatabase, get, set, update, remove } from "firebase/database";
import { toastrError, toastrSuccess } from "../components/toastr";
import { v4 } from "uuid";

function ConfirmPopup({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-5">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
        <p className="text-sm font-bold text-gray-900 mb-1">Are you sure?</p>
        <p className="text-sm text-gray-500 mb-5">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function TableSettings() {
  const navigate = useNavigate();
  const ownerId = localStorage.getItem("ownerId");
  const db = getDatabase();

  const [tables, setTables] = useState([]);
  const [tableNum, setTableNum] = useState("");
  const [tableLabel, setTableLabel] = useState("");
  const [tableCapacity, setTableCapacity] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [confirm, setConfirm] = useState(null);

  const loadTables = useCallback(async () => {
    const snap = await get(ref(db, `tables/${ownerId}`));
    setTables(snap.exists() ? Object.values(snap.val()) : []);
  }, [db, ownerId]);

  useEffect(() => {
    if (ownerId) loadTables();
  }, [ownerId, loadTables]);

  async function handleAdd() {
    if (!tableNum.trim()) return;
    try {
      const id = v4();
      await set(ref(db, `tables/${ownerId}/${id}`), {
        id,
        number: tableNum.trim(),
        label: tableLabel.trim(),
        capacity: tableCapacity ? parseInt(tableCapacity, 10) : null,
        status: "free",
        currentCustomerId: null,
      });
      setTableNum("");
      setTableLabel("");
      setTableCapacity("");
      await loadTables();
    } catch (err) {
      toastrError("Could not add table");
    }
  }

  async function handleSaveEdit(table) {
    try {
      await update(ref(db, `tables/${ownerId}/${table.id}`), {
        number: editData.number?.trim() || table.number,
        label: editData.label?.trim() ?? table.label,
        capacity: editData.capacity ? parseInt(editData.capacity, 10) : table.capacity,
      });
      setEditingId(null);
      await loadTables();
      toastrSuccess("Table updated");
    } catch (err) {
      toastrError("Could not update table");
    }
  }

  function confirmRemove(table) {
    setConfirm({
      message: `Remove Table ${table.number}${table.label ? ` (${table.label})` : ""}? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await remove(ref(db, `tables/${ownerId}/${table.id}`));
          await loadTables();
        } catch (err) {
          toastrError("Could not remove table");
        }
        setConfirm(null);
      },
    });
  }

  return (
    <div className="max-w-md mx-auto pb-10">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button
          onClick={() => navigate("/settings")}
          className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Table Configuration</h1>
          <p className="text-xs text-gray-500">Add, edit and remove tables</p>
        </div>
      </div>

      <div className="px-5 flex flex-col gap-y-4">
        {/* Add table */}
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
          <h2 className="text-sm font-bold text-gray-900 mb-3">Add Table</h2>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-500 mb-1 block">
                  Table Number *
                </label>
                <input
                  value={tableNum}
                  onChange={(e) => setTableNum(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
                  placeholder="e.g. 1"
                />
              </div>
              <div className="w-20">
                <label className="text-xs font-medium text-gray-500 mb-1 block">
                  Seats
                </label>
                <input
                  value={tableCapacity}
                  onChange={(e) => setTableCapacity(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
                  placeholder="4"
                  type="number"
                  min="1"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">
                Label{" "}
                <span className="text-gray-400">(optional — e.g. Window, Patio, VIP)</span>
              </label>
              <input
                value={tableLabel}
                onChange={(e) => setTableLabel(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
                placeholder="e.g. Window seat"
              />
            </div>
            <button
              onClick={handleAdd}
              disabled={!tableNum.trim()}
              className={`w-full py-2.5 rounded-xl text-sm font-semibold ${
                tableNum.trim()
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              + Add Table
            </button>
          </div>
        </div>

        {/* Tables list */}
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
          <h2 className="text-sm font-bold text-gray-900 mb-3">
            Tables ({tables.length})
          </h2>
          {tables.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No tables configured yet.
            </p>
          )}
          {tables.map((t) => (
            <div
              key={t.id}
              className="border border-gray-100 rounded-xl px-4 py-3 mb-2"
            >
              {editingId === t.id ? (
                /* Edit mode */
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-xs text-gray-400 mb-1 block">Table #</label>
                      <input
                        value={editData.number ?? t.number}
                        onChange={(e) =>
                          setEditData((d) => ({ ...d, number: e.target.value }))
                        }
                        autoFocus
                        className="w-full px-3 py-2 bg-gray-50 rounded-lg border border-primary text-sm outline-none"
                      />
                    </div>
                    <div className="w-20">
                      <label className="text-xs text-gray-400 mb-1 block">Seats</label>
                      <input
                        value={editData.capacity ?? (t.capacity || "")}
                        onChange={(e) =>
                          setEditData((d) => ({ ...d, capacity: e.target.value }))
                        }
                        className="w-full px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm outline-none focus:border-primary"
                        type="number"
                        min="1"
                        placeholder="4"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Label</label>
                    <input
                      value={editData.label ?? (t.label || "")}
                      onChange={(e) =>
                        setEditData((d) => ({ ...d, label: e.target.value }))
                      }
                      className="w-full px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm outline-none focus:border-primary"
                      placeholder="e.g. Window seat"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSaveEdit(t)}
                      className="flex-1 py-2 bg-primary text-white text-xs font-semibold rounded-lg"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="flex-1 py-2 bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* Display mode */
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-semibold text-gray-800">
                      Table {t.number}
                    </span>
                    {t.label && (
                      <span className="text-xs text-gray-500 ml-2">{t.label}</span>
                    )}
                    {t.capacity && (
                      <span className="text-xs text-gray-400 ml-2">
                        · {t.capacity} seats
                      </span>
                    )}
                    <span
                      className={`ml-2 text-xs font-medium ${
                        t.status === "free" ? "text-green-500" : "text-orange-500"
                      }`}
                    >
                      · {t.status === "free" ? "Free" : "Occupied"}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingId(t.id);
                        setEditData({});
                      }}
                      className="text-xs text-primary font-medium border border-purple-200 rounded-lg px-2.5 py-1.5"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => confirmRemove(t)}
                      disabled={t.status !== "free"}
                      className={`text-xs font-medium border rounded-lg px-2.5 py-1.5 ${
                        t.status === "free"
                          ? "text-red-500 border-red-200"
                          : "text-gray-300 border-gray-200 cursor-not-allowed"
                      }`}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {confirm && (
        <ConfirmPopup
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

export default TableSettings;
